import { SearchableMultiSelect } from '@components/ui/searchable-multi-select';
import { useStore } from '@store/index';
import { ChevronRight, Users } from 'lucide-react';
import React, { useCallback, useState } from 'react';

import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';

import { EmptyState } from './components/EmptyState';
import { FilterBar } from './components/FilterBar';
import type { KpiFilter } from './components/KpiStrip';
import { KpiStrip } from './components/KpiStrip';
import { RefreshIndicator } from './components/RefreshIndicator';
import { KpiStripSkeleton } from './components/skeletons/KpiStripSkeleton';
import { WorkforceGridSkeleton } from './components/skeletons/WorkforceGridSkeleton';
import { WorkforceGrid } from './components/WorkforceGrid';
import type { CaseItem } from './data/types';
import { useBulkReassign } from './hooks/useBulkReassign';
import { useMyAvailability } from './hooks/useMyAvailability';
import { useTeamAgents } from './hooks/useTeamAgents';
import { useWorkforceCases } from './hooks/useWorkforceCases';
import { useWorkforceReassign } from './hooks/useWorkforceReassign';
import { useWorkforceSelection } from './hooks/useWorkforceSelection';

export function WorkforceContainer() {
  const { user } = useStore();

  // KPI tile → risk filter. `'all'` means show all risk levels (OPEN selected).
  const [riskFilter, setRiskFilter] = useState<KpiFilter>('all');

  const {
    selectedOrganizationId,
    eligibleTeams,
    programs,
    selectedTeams,
    scopeTeams,
    teamProgramMatches,
    programOptions,
    programNameById,
    teamSelectOptions,
    teamNameById,
    slaPairs,
    programCasesTuples,
    selectedTeamIds,
    workforceSelectedProgramIds,
    setWorkforceSelectedTeamIds,
    setWorkforceSelectedProgramIds,
    teamsLoading,
  } = useWorkforceSelection();

  const {
    cases,
    filteredCases,
    kpi,
    rawCasesById,
    isInitialLoading: casesInitialLoading,
    isRefreshing,
    isError: hasFetchError,
    isOverFanout,
  } = useWorkforceCases({
    tuples: programCasesTuples,
    slaPairs,
    programNameById,
    currentUserId: user?.id ?? null,
    selectedOrganizationId,
    riskFilter,
    selectedProgramIds: workforceSelectedProgramIds,
    scopeTeams,
    programs,
  });

  const { isAvailable: meAvailable } = useMyAvailability();

  const { agents: teamAgents, currentUserAgent } = useTeamAgents({
    teams: selectedTeams,
    cases,
    currentUserId: user?.id ?? null,
    meAvailable,
  });

  const { handleReassign, handleReassignNoInvalidate, invalidatePrograms, isReassigning } =
    useWorkforceReassign({
      orgId: selectedOrganizationId,
      rawCasesById,
      eligibleTeams,
      programs,
    });

  const { run: bulkRun, inFlight: bulkInFlight } = useBulkReassign({
    reassignOne: handleReassignNoInvalidate,
    invalidatePrograms,
  });

  const onBulkReassign = useCallback(
    (targets: CaseItem[], newAgentId: string) => bulkRun(targets, newAgentId),
    [bulkRun],
  );

  const isInitialLoading = teamsLoading || casesInitialLoading;

  const onlineKpi = {
    open: kpi.open,
    atRisk: kpi.atRisk,
    breached: kpi.breached,
    online: teamAgents.filter(a => a.online).length,
    totalAgents: teamAgents.length,
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-background">
      <BreadcrumbPortal>
        <Breadcrumb>
          <Breadcrumb.Item>Workforce Console</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbPortal>

      <div className="shrink-0 px-6 pt-5 pb-4 border-b border-border bg-background">
        <div className="flex items-center gap-2 flex-wrap">
          <SearchableMultiSelect
            selected={selectedTeamIds}
            onChange={setWorkforceSelectedTeamIds}
            options={teamSelectOptions}
            placeholder={teamsLoading ? 'Loading teams…' : 'Select teams'}
            searchPlaceholder="Search teams..."
            icon={Users}
            className="w-[260px] font-semibold"
            showClearAll
            disabled={teamsLoading}
          />
          <ChevronRight className="size-4 text-muted-foreground" aria-hidden="true" />
          <FilterBar
            selectedPrograms={workforceSelectedProgramIds}
            onProgramsChange={setWorkforceSelectedProgramIds}
            programOptions={programOptions}
          />
          {isRefreshing && (
            <span className="ml-auto">
              <RefreshIndicator />
            </span>
          )}
        </div>

        <div className="mt-4">
          {isInitialLoading ? (
            <KpiStripSkeleton />
          ) : (
            <KpiStrip {...onlineKpi} activeFilter={riskFilter} onFilterChange={setRiskFilter} />
          )}
        </div>
      </div>

      {hasFetchError && (
        <div
          role="alert"
          className="shrink-0 px-6 py-2.5 border-b border-critical-border bg-critical-muted text-sm text-critical-foreground"
        >
          Some data couldn&apos;t be loaded. Try refreshing the page; if the problem persists,
          contact support.
        </div>
      )}

      <div className="flex-1 min-h-0">
        {isInitialLoading ? (
          <WorkforceGridSkeleton />
        ) : selectedTeams.length === 0 ? (
          <EmptyState
            title="Select a team to get started"
            description="Pick one or more teams from the dropdown above to see their workforce."
          />
        ) : teamProgramMatches.length === 0 ? (
          <EmptyState
            title="No linked programs"
            description="The selected teams aren't linked to any program in this organization yet."
          />
        ) : isOverFanout ? (
          <EmptyState
            title="Too many programs selected"
            description="The selected teams span more programs than the console can load at once. Select fewer teams."
          />
        ) : (
          <WorkforceGrid
            agents={teamAgents}
            currentUserAgent={currentUserAgent}
            cases={filteredCases}
            countedCases={cases}
            teams={selectedTeams}
            teamNameById={teamNameById}
            onReassign={handleReassign}
            onBulkReassign={onBulkReassign}
            bulkInFlight={bulkInFlight}
            isReassigning={isReassigning}
            emptyState={
              <EmptyState
                title="No open cases"
                description="No cases match the current filters. Try clearing the program filter or selecting more teams."
              />
            }
          />
        )}
      </div>
    </div>
  );
}
